<template>
    <div class="template-analytics">
        <a-spin :spinning="loading">
            <div class="info-banner">
                <a-alert
                    :message="$t('whatsapp_business_api.metaInsightsTemplateAboutTitle')"
                    :description="$t('whatsapp_business_api.metaInsightsTemplateAboutDesc')"
                    type="info"
                    show-icon
                />
            </div>

            <div class="template-selector">
                <label>{{ $t('whatsapp_business_api.metaInsightsTemplateSelectLabel') }}</label>
                <a-select
                    mode="multiple"
                    :value="selectedTemplateIds"
                    @change="onTemplateSelect"
                    :placeholder="$t('whatsapp_business_api.metaInsightsTemplateSelectPlaceholder')"
                    style="width: 100%"
                    :max-tag-count="5"
                >
                    <a-select-option v-for="template in templates" :key="template.id" :value="template.id">
                        {{ template.name }} ({{ template.language }})
                    </a-select-option>
                </a-select>
                <a-button type="primary" @click="fetchData" :disabled="selectedTemplateIds.length === 0" class="fetch-btn">
                    {{ $t('whatsapp_business_api.metaInsightsFetchData') }}
                </a-button>
            </div>

            <div v-if="!loading && data && data.data && data.data.length > 0">
                <div class="toolbar">
                    <div class="buttons">
                        <IconButton
                            :classNames="!tableMode ? 'selected' : ''"
                            :onClick="() => (tableMode = false)"
                            v-tippy="{ content: $t('whatsapp_business_api.metaInsightsViewChart') }"
                        >
                            <md-icon>insights</md-icon>
                        </IconButton>
                        <IconButton
                            :classNames="tableMode ? 'selected' : ''"
                            :onClick="() => (tableMode = true)"
                            v-tippy="{ content: $t('whatsapp_business_api.metaInsightsViewTable') }"
                        >
                            <md-icon>table_view</md-icon>
                        </IconButton>
                        <IconButton :onClick="exportToExcel" v-tippy="{ content: $t('whatsapp_business_api.metaInsightsDownloadExcel') }">
                            <md-icon>download</md-icon>
                        </IconButton>
                    </div>
                </div>

                <div class="stats-row">
                    <StatCard :title="$t('whatsapp_business_api.metaInsightsTotalSent')" :value="totalSent" :unit="$t('whatsapp_business_api.metaInsightsUnitMessage')" />
                    <StatCard :title="$t('whatsapp_business_api.metaInsightsTotalDelivered')" :value="totalDelivered" :unit="$t('whatsapp_business_api.metaInsightsUnitMessage')" />
                    <StatCard :title="$t('whatsapp_business_api.metaInsightsTotalRead')" :value="totalRead" :unit="$t('whatsapp_business_api.metaInsightsUnitMessage')" />
                    <StatCard :title="$t('whatsapp_business_api.metaInsightsReadRate')" :value="readRate" unit="%" />
                </div>

                <div class="chart-container" v-if="!tableMode">
                    <StackedAreaChart
                        :title="$t('whatsapp_business_api.metaInsightsTemplatePerformance')"
                        :x-axis-data="getXAxisData()"
                        :legends="[$t('whatsapp_business_api.metaInsightsSent'), $t('whatsapp_business_api.metaInsightsDelivered'), $t('whatsapp_business_api.metaInsightsRead')]"
                        :colors="['#37A2FF', '#80FFA5', '#FFBF00']"
                        :series="getSeries()"
                    />
                </div>

                <div class="table-container" v-else>
                    <a-table :columns="columns" :data-source="tableData" :pagination="{ pageSize: 10 }" size="middle" />
                </div>

                <div class="cost-section" v-if="hasCostData">
                    <h3>{{ $t('whatsapp_business_api.metaInsightsCostInfo') }}</h3>
                    <div class="cost-cards">
                        <div class="cost-card">
                            <div class="cost-label">{{ $t('whatsapp_business_api.metaInsightsTotalSpent') }}</div>
                            <div class="cost-value">${{ totalAmountSpent.toFixed(2) }}</div>
                        </div>
                        <div class="cost-card">
                            <div class="cost-label">{{ $t('whatsapp_business_api.metaInsightsCostPerDelivered') }}</div>
                            <div class="cost-value">${{ avgCostPerDelivered.toFixed(4) }}</div>
                        </div>
                    </div>
                </div>

                <div class="click-section" v-if="hasClickData">
                    <h3>{{ $t('whatsapp_business_api.metaInsightsClickInfo') }}</h3>
                    <div class="click-info">
                        <p>
                            {{ $t('whatsapp_business_api.metaInsightsClickInfoDesc') }}
                        </p>
                    </div>
                </div>
            </div>

            <div v-else-if="!loading" class="empty-state">
                <a-empty :description="$t('whatsapp_business_api.metaInsightsTemplateNoData')" />
            </div>
        </a-spin>
    </div>
</template>

<script>
import Vue from "vue";
import moment from "moment";
import _ from "lodash";
import { expertToExcel } from "../../../../../utils/utils";
import StackedAreaChart from "../../../Chatbot/pages/reports/charts/StackedAreaChart.vue";
import PieChartV2 from "../../../Chatbot/pages/reports/charts/PieChartV2.vue";
import StatCard from "../../../Chatbot/pages/reports/charts/StatCard.vue";
import IconButton from "../../../Settings/components/IconButton.vue";

export default Vue.extend({
    name: "TemplateAnalytics",
    components: {
        StackedAreaChart,
        StatCard,
        IconButton,
    },
    props: {
        channel: {
            type: Object,
            required: true,
        },
        filters: {
            type: Object,
            required: true,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        data: {
            type: Object,
            default: null,
        },
        templates: {
            type: Array,
            default: function () {
                return [];
            },
        },
        selectedTemplateIds: {
            type: Array,
            default: function () {
                return [];
            },
        },
    },
    data() {
        return {
            tableMode: false,
        };
    },
    computed: {
        columns() {
            return [
                {
                    title: this.$t("whatsapp_business_api.metaInsightsDateColumn"),
                    dataIndex: "date",
                    key: "date",
                },
                {
                    title: this.$t("whatsapp_business_api.metaInsightsTemplateId"),
                    dataIndex: "templateId",
                    key: "templateId",
                },
                {
                    title: this.$t("whatsapp_business_api.metaInsightsSent"),
                    dataIndex: "sent",
                    key: "sent",
                    sorter: (a, b) => a.sent - b.sent,
                },
                {
                    title: this.$t("whatsapp_business_api.metaInsightsDelivered"),
                    dataIndex: "delivered",
                    key: "delivered",
                    sorter: (a, b) => a.delivered - b.delivered,
                },
                {
                    title: this.$t("whatsapp_business_api.metaInsightsRead"),
                    dataIndex: "read",
                    key: "read",
                    sorter: (a, b) => a.read - b.read,
                },
            ];
        },
        dataPoints() {
            const data = _.get(this.data, "data", []) || [];
            if (data.length > 0) {
                return _.get(data, "0.data_points", []) || [];
            }
            return [];
        },
        totalSent() {
            return this.dataPoints.reduce((sum, dp) => sum + (dp.sent || 0), 0);
        },
        totalDelivered() {
            return this.dataPoints.reduce((sum, dp) => sum + (dp.delivered || 0), 0);
        },
        totalRead() {
            return this.dataPoints.reduce((sum, dp) => sum + (dp.read || 0), 0);
        },
        readRate() {
            if (this.totalDelivered === 0) return 0;
            return ((this.totalRead / this.totalDelivered) * 100).toFixed(2);
        },
        hasCostData() {
            return this.dataPoints.some((dp) => dp.cost && dp.cost.length > 0);
        },
        hasClickData() {
            return this.dataPoints.some((dp) => dp.clicked && dp.clicked.length > 0);
        },
        totalAmountSpent() {
            let total = 0;
            this.dataPoints.forEach((dp) => {
                if (dp.cost && dp.cost.length > 0) {
                    const amountSpent = dp.cost.find((c) => c.type === "amount_spent");
                    if (amountSpent) {
                        total += amountSpent.value || 0;
                    }
                }
            });
            return total;
        },
        avgCostPerDelivered() {
            if (this.totalDelivered === 0) return 0;
            return this.totalAmountSpent / this.totalDelivered;
        },
        tableData() {
            return this.dataPoints.map((dp, index) => ({
                key: index,
                date: moment.unix(dp.start).format("DD/MM/YYYY"),
                templateId: dp.template_id || "-",
                sent: dp.sent || 0,
                delivered: dp.delivered || 0,
                read: dp.read || 0,
            }));
        },
    },
    methods: {
        getXAxisData() {
            const dates = _.uniq(this.dataPoints.map((dp) => moment.unix(dp.start).format("DD/MM")));
            return dates;
        },
        getSeries() {
            const dates = _.uniq(this.dataPoints.map((dp) => moment.unix(dp.start).format("DD/MM")));

            const sentData = dates.map((date) => {
                const points = this.dataPoints.filter((dp) => moment.unix(dp.start).format("DD/MM") === date);
                return points.reduce((sum, p) => sum + (p.sent || 0), 0);
            });

            const deliveredData = dates.map((date) => {
                const points = this.dataPoints.filter((dp) => moment.unix(dp.start).format("DD/MM") === date);
                return points.reduce((sum, p) => sum + (p.delivered || 0), 0);
            });

            const readData = dates.map((date) => {
                const points = this.dataPoints.filter((dp) => moment.unix(dp.start).format("DD/MM") === date);
                return points.reduce((sum, p) => sum + (p.read || 0), 0);
            });

            return [
                {
                    name: this.$t("whatsapp_business_api.metaInsightsSent"),
                    type: "line",
                    smooth: true,
                    showSymbol: true,
                    emphasis: { focus: "series" },
                    data: sentData,
                },
                {
                    name: this.$t("whatsapp_business_api.metaInsightsDelivered"),
                    type: "line",
                    smooth: true,
                    showSymbol: true,
                    emphasis: { focus: "series" },
                    data: deliveredData,
                },
                {
                    name: this.$t("whatsapp_business_api.metaInsightsRead"),
                    type: "line",
                    smooth: true,
                    showSymbol: true,
                    emphasis: { focus: "series" },
                    data: readData,
                },
            ];
        },
        exportToExcel() {
            const exportData = this.tableData.map((row) => ({
                [this.$t("whatsapp_business_api.metaInsightsDateColumn")]: row.date,
                [this.$t("whatsapp_business_api.metaInsightsTemplateId")]: row.templateId,
                [this.$t("whatsapp_business_api.metaInsightsSent")]: row.sent,
                [this.$t("whatsapp_business_api.metaInsightsDelivered")]: row.delivered,
                [this.$t("whatsapp_business_api.metaInsightsRead")]: row.read,
            }));
            expertToExcel(exportData, "template_analytics.xlsx", this.$t("whatsapp_business_api.metaInsightsTabTemplate"));
        },
        onTemplateSelect(selectedIds) {
            const limitedIds = selectedIds.slice(0, 10);
            this.$emit("update:selectedTemplateIds", limitedIds);
        },
        fetchData() {
            this.$emit("fetch");
        },
    },
});
</script>

<style scoped lang="scss">
.template-analytics {
    padding: 20px;

    .info-banner {
        margin-bottom: 20px;
    }

    .template-selector {
        display: flex;
        gap: 15px;
        align-items: flex-end;
        margin-bottom: 20px;
        padding: 15px;
        background: var(--color-bg-subtle);
        border-radius: 8px;

        label {
            font-size: 12px;
            font-weight: 600;
            color: var(--color-text-default);
            margin-bottom: 6px;
            display: block;
        }

        .ant-select {
            flex: 1;
        }

        .fetch-btn {
            flex-shrink: 0;
        }
    }

    .toolbar {
        display: flex;
        justify-content: flex-end;
        margin-bottom: 20px;

        .buttons {
            display: flex;
            border: 1px solid var(--color-border-default);
            border-radius: 4px;

            .icon-button {
                padding: 8px;
                display: flex;
                justify-content: center;
                align-items: center;
                border-right: 1px solid var(--color-border-default);
                border-radius: unset !important;

                &.selected {
                    background-color: var(--color-bg-info-low-hover);
                }

                &:last-child {
                    border-right: none;
                }

                &:hover {
                    background-color: var(--color-bg-neutral-pressed);
                }
            }
        }
    }

    .stats-row {
        display: flex;
        gap: 20px;
        margin-bottom: 30px;
        flex-wrap: wrap;

        .stat-card {
            flex: 1;
            min-width: 180px;
        }
    }

    .chart-container {
        min-height: 400px;
        margin-bottom: 30px;
    }

    .table-container {
        margin-bottom: 30px;
    }

    .cost-section,
    .click-section {
        margin-top: 30px;
        padding-top: 20px;
        border-top: 1px solid var(--color-border-default);

        h3 {
            font-size: 16px;
            font-weight: 600;
            margin-bottom: 15px;
        }

        .cost-cards {
            display: flex;
            gap: 15px;
            flex-wrap: wrap;

            .cost-card {
                padding: 15px 20px;
                background: var(--color-bg-subtle);
                border-radius: 8px;
                min-width: 150px;

                .cost-label {
                    font-size: 12px;
                    color: var(--color-text-default);
                    margin-bottom: 4px;
                }

                .cost-value {
                    font-size: 20px;
                    font-weight: 700;
                    color: var(--color-text-info);
                }
            }
        }

        .click-info {
            padding: 15px;
            background: var(--color-bg-subtle);
            border-radius: 8px;

            p {
                margin: 0;
                color: var(--color-text-default);
            }
        }
    }

    .empty-state {
        padding: 60px 20px;
        text-align: center;
    }
}
</style>
